import React, { useState, useEffect } from 'react';
import ComboBusca from '../../ComboBusca';
import useEhCompacto from '../../../utils/useEhCompacto';

// =====================================================================
// Seletor de peça da Central de Pedidos — comboboxes Fornecedor / Família / Peça.
// Extraído de CertificacaoQuestionario.js (Etapa 1) em 2026-07-20 para ser
// COMPARTILHADO com CertificacaoCertificar.js (Etapa 2 — "Peças substituídas"):
// o mesmo seletor de origem 'Central' passa a valer nas duas etapas, sem duplicar
// código. Comportamento idêntico ao original.
// =====================================================================

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Catálogo da Central de Pedidos — carregado UMA vez por sessão de tela e
// compartilhado por todas as linhas de peça. ⚠️ Eram ~840 peças quando isto foi
// escrito; medido em 03/09/2026 são **7.452**. Segue em uma requisição só (a
// lista é filtrada em memória pelos comboboxes), mas já não é "leve": se um dia
// pesar na abertura da tela, o conserto é busca no servidor, não mais cache.
//
// ⚠️⚠️ O CACHE É POR ENDEREÇO, e isso não é preciosismo: existem DUAS leituras
// do mesmo catálogo — `/catalogo` (com `PRECO_REF`, sem o Gerente) e
// `/catalogo/selecao` (sem preço nenhum, com o Gerente). Com um cache único, a
// primeira tela a carregar serviria a segunda: ou o gerente receberia a lista
// com preço, ou a Certificação perderia o preço da tela. Ver a nota do
// `GET /catalogo/selecao` em server/compras/server-compras.js.
export const ENDPOINT_COM_PRECO = '/api/compras/catalogo';
export const ENDPOINT_SEM_PRECO = '/api/compras/catalogo/selecao';

const catalogoCentralCache = new Map(); // endpoint → lista
export const carregarCatalogoCentral = async (endpoint = ENDPOINT_COM_PRECO) => {
  if (catalogoCentralCache.has(endpoint)) return catalogoCentralCache.get(endpoint);
  let lista = [];
  try {
    const r = await fetch(endpoint, { headers: auth() });
    lista = r.ok ? await r.json() : [];
  } catch { lista = []; }
  catalogoCentralCache.set(endpoint, lista);
  return lista;
};


// O combobox com busca virou componente compartilhado em 2026-07-28
// (`src/components/ComboBusca.js`) — o formulário de Atestados precisava do
// mesmo comportamento no seletor de funcionário. Aqui só se consome.

// Escolha de peça da Central de Pedidos: comboboxes Fornecedor / Família / Peça.
// Os filtros são BIDIRECIONAIS (2026-07-03): escolher a Família também restringe
// os fornecedores àqueles que a têm (ex.: família de fritadeira → só MASTER e
// UE ELETRONICA), e vice-versa. Ambos opcionais — dá para ir direto na Peça.
// `endpoint`: de qual leitura do catálogo esta tela se serve. O padrão é a COM
// preço (a Certificação, que exibe o preço de referência); a Solicitação de
// Reparo passa a SEM preço, porque ela também é preenchida pelo Gerente.
const SelecaoPecaCentral = ({ onEscolher, endpoint = ENDPOINT_COM_PRECO }) => {
  const [catalogo, setCatalogo] = useState(null); // null = carregando
  const [fornecedor, setFornecedor] = useState('');
  const [familia, setFamilia] = useState('');
  const ehCompacto = useEhCompacto(); // celular/tablet: empilha Fornecedor/Família/Peça

  useEffect(() => { carregarCatalogoCentral(endpoint).then(setCatalogo); }, [endpoint]);

  if (catalogo === null) return <span style={{ fontSize: 13, color: '#94a3b8' }}>Carregando catálogo da central…</span>;
  if (catalogo.length === 0) return <span style={{ fontSize: 13, color: '#b91c1c' }}>Catálogo da central indisponível — tente recarregar a página.</span>;

  const doFornecedor = (c) => !fornecedor || c.FORNECEDOR === fornecedor;
  const daFamilia = (c) => !familia || c.FAMILIA === familia;
  // Cada lista é filtrada pelo OUTRO filtro (bidirecional) — a seleção atual
  // nunca fica incompatível, então trocar um não precisa limpar o outro.
  const fornecedores = [...new Set(catalogo.filter(daFamilia).map((c) => c.FORNECEDOR))].sort();
  const familias = [...new Set(catalogo.filter(doFornecedor).map((c) => c.FAMILIA).filter(Boolean))].sort();
  const pecas = catalogo.filter((c) => doFornecedor(c) && daFamilia(c));

  const opcaoTodos = (rotulo) => ({ key: '', label: rotulo, valor: '' });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '150px 190px 1fr', gap: 6, flex: 1 }}>
      <ComboBusca valor={fornecedor} placeholder="Fornecedor (todos)" onMudar={setFornecedor}
        opcoes={[opcaoTodos('(todos os fornecedores)'), ...fornecedores.map((f) => ({ key: f, label: f, valor: f }))]} />
      <ComboBusca valor={familia} placeholder="Família (todas)" onMudar={setFamilia}
        opcoes={[opcaoTodos('(todas as famílias)'), ...familias.map((f) => ({ key: f, label: f, valor: f }))]} />
      {/* valor sempre vazio — ao escolher, esta linha vira os campos travados */}
      <ComboBusca valor="" placeholder={`Peça (${pecas.length}) — digite ou escolha…`}
        opcoes={pecas.map((c) => ({ key: c.ID, label: `${c.DESCRICAO} — ${c.CODIGO}`, valor: c }))}
        onMudar={(p) => { if (p) onEscolher(p); }} />
    </div>
  );
};

export default SelecaoPecaCentral;
